import type { MoneyCents } from '../core/types';

export const START_BALANCE_CENTS = 100_000;

export interface RankedRun {
  readonly id: string;
  readonly username: string;
  readonly betCents: MoneyCents;
  readonly startBalanceCents: MoneyCents;
  readonly startedAt: number;
}

export interface CompletedRankedRun extends RankedRun {
  readonly finalBalanceCents: MoneyCents;
  readonly profitCents: MoneyCents;
  /** Display convenience only. Sorting must compare profit/bet as fractions. */
  readonly xValue: number;
  readonly completedAt: number;
}

function cleanName(username: string): string {
  const value = username.trim();
  if (value.length < 1 || value.length > 20) {
    throw new RangeError('Der Duellantenname muss 1 bis 20 Zeichen lang sein.');
  }
  return value;
}

function assertMoney(value: MoneyCents, label: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new RangeError(`${label} muss ein nicht-negativer, sicherer Centbetrag sein.`);
  }
}

export function startRankedRun(input: {
  readonly id: string;
  readonly username: string;
  readonly betCents: MoneyCents;
  readonly startedAt: number;
}): RankedRun {
  if (input.id.trim().length === 0) throw new RangeError('Eine Ranked-ID ist erforderlich.');
  assertMoney(input.betCents, 'Der Ranked-Einsatz');
  if (input.betCents === 0) throw new RangeError('Der Ranked-Einsatz muss größer als null sein.');
  if (!Number.isSafeInteger(input.startedAt) || input.startedAt <= 0) {
    throw new RangeError('Der Ranked-Startzeitpunkt ist ungültig.');
  }

  return Object.freeze({
    id: input.id,
    username: cleanName(input.username),
    betCents: input.betCents,
    startBalanceCents: START_BALANCE_CENTS,
    startedAt: input.startedAt,
  });
}

export function finishRankedRun(
  run: RankedRun,
  finalBalanceCents: MoneyCents,
  completedAt: number,
): CompletedRankedRun | null {
  assertMoney(finalBalanceCents, 'Das Abschlussguthaben');
  if (!Number.isSafeInteger(completedAt) || completedAt < run.startedAt) {
    throw new RangeError('Der Ranked-Abschlusszeitpunkt ist ungültig.');
  }
  const profitCents = finalBalanceCents - run.startBalanceCents;
  if (profitCents <= 0) return null;

  return Object.freeze({
    ...run,
    finalBalanceCents,
    profitCents,
    xValue: profitCents / run.betCents,
    completedAt,
  });
}
